import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type SurveyEntryChromeMode = 'entry' | 'completion'

interface SurveyEntryChromeContextValue {
  mode: SurveyEntryChromeMode
  setMode: (mode: SurveyEntryChromeMode) => void
}

const SurveyEntryChromeContext = createContext<SurveyEntryChromeContextValue | null>(null)

export function SurveyEntryChromeProvider({ children }: { readonly children: ReactNode }) {
  const [mode, setMode] = useState<SurveyEntryChromeMode>('entry')
  const value = useMemo(() => ({ mode, setMode }), [mode])
  return (
    <SurveyEntryChromeContext.Provider value={value}>{children}</SurveyEntryChromeContext.Provider>
  )
}

export function useSurveyEntryChrome(): SurveyEntryChromeContextValue {
  const ctx = useContext(SurveyEntryChromeContext)
  if (!ctx) {
    throw new Error('useSurveyEntryChrome must be used within SurveyEntryChromeProvider')
  }
  return ctx
}

/** Optional hook for pages that may render outside the provider in tests. */
export function useSurveyEntryChromeOptional(): SurveyEntryChromeContextValue | null {
  return useContext(SurveyEntryChromeContext)
}

/** Switches layout to completion shell (no navy entry band) — same as post-submit on /survey. */
export function useSurveyEntryCompletionChrome(active: boolean): void {
  const chrome = useSurveyEntryChromeOptional()
  useEffect(() => {
    if (!chrome) return
    if (active) {
      chrome.setMode('completion')
      return () => {
        chrome.setMode('entry')
      }
    }
    chrome.setMode('entry')
  }, [active, chrome])
}
